import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/Block";
import request from "../../../Helpers/API";
import PlantillaScreenRemastered from "../../CRM/PlantillaScreenRemastered";
import { LoaderScreen } from "../../components/LoaderScreen";
import PaginadorListado from "../../components/PaginadorListado";
import FiltroPanel, { contarFiltrosActivos } from "../../components/Filtro/FiltroPanel";
import {
  FINANCIERAS_DOCUMENTOS,
  TIPOS_PERSONA_DOCUMENTO,
  LINEAS_CREDITO_DOCUMENTO,
  etiquetaCatalogo,
} from "../../../Arrays/DocumentosCreditoArray";
import { ModalDocumentos } from "./ModalDocumentos";

// Crédito › Listado de documentos requeridos por financiera (/Credito/Documentos).
// Rediseñado: 2026-09-16 — IA (Claude). Base: PlantillaScreenRemastered (igual que ReportePrestamosClientes).
// Actualizado: 2026-09-17 — IA (Claude): columna y filtro de "Cobranza" (documento_cobranza)
// y botón Deshabilitar por renglón.
const TITULO_SWAL = "DOCUMENTOS";
const RUTA_REGRESO = "/Credito/Main";
const MENSAJE_ERROR_GENERICO =
  "No fue posible completar la operación. Intente nuevamente o contacte al administrador.";
const REGISTROS_POR_PAGINA = 50;
// Baja lógica: el backend deshabilita el documento y regresa el listado ya actualizado.
// ⚠ Confirmar el nombre exacto con el backend: es lo único que hay que ajustar aquí.
const ENDPOINT_DESHABILITAR = "Documentos/Deshabilitar";
const SIN_EDICION = -999;

// Filtros locales (se aplican sobre el listado de la financiera seleccionada).
const FILTROS_INICIALES = { tipopersona: "", linea_credito: "", opcional: "", fase2: "", documento_cobranza: "" };
const OPCIONES_SI_NO = [
  { value: "1", label: "SÍ" },
  { value: "0", label: "NO" },
];
const CAMPOS_FILTRO = [
  { name: "tipopersona", label: "Tipo de persona", tipo: "select", opciones: TIPOS_PERSONA_DOCUMENTO, opcionTodos: "TODOS" },
  { name: "linea_credito", label: "Línea de crédito", tipo: "select", opciones: LINEAS_CREDITO_DOCUMENTO, opcionTodos: "TODAS" },
  { name: "opcional", label: "Opcional", tipo: "select", opciones: OPCIONES_SI_NO, opcionTodos: "TODOS" },
  { name: "fase2", label: "Fase 2", tipo: "select", opciones: OPCIONES_SI_NO, opcionTodos: "TODOS" },
  { name: "documento_cobranza", label: "Cobranza", tipo: "select", opciones: OPCIONES_SI_NO, opcionTodos: "TODOS" },
];

const COLUMNAS_BANDERA = [
  { campo: "opcional", etiqueta: "Opcional" },
  { campo: "documentacion", etiqueta: "Documentación" },
  { campo: "fase2", etiqueta: "Fase 2" },
  { campo: "documento_cobranza", etiqueta: "Cobranza" },
];

// Escapa texto para mostrarlo dentro de html de SweetAlert.
const escaparHtml = (texto) =>
  String(texto ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const mostrarError = (error) => {
  const mensaje = typeof error?.mensaje === "string" && error.mensaje.trim() ? error.mensaje : MENSAJE_ERROR_GENERICO;
  Swal.fire(TITULO_SWAL, mensaje, "error");
};

const coincideBandera = (valorFiltro, valorFila) =>
  valorFiltro === "" || (valorFiltro === "1") === Boolean(valorFila);

const Bandera = ({ activo }) =>
  activo ? (
    <CheckCircleIcon className="sdoc-bandera sdoc-bandera--si" titleAccess="Sí" />
  ) : (
    <RemoveCircleIcon className="sdoc-bandera sdoc-bandera--no" titleAccess="No" />
  );

export const ScreenDocumentos = () => {
  const history = useHistory();
  const [financiera, setFinanciera] = useState(FINANCIERAS_DOCUMENTOS[0].value);
  const [listado, setListado] = useState([]);
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(REGISTROS_POR_PAGINA);
  const [modal, setModal] = useState({ open: false, iddocumento: SIN_EDICION });
  const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });

  const mostrarLoader = (mostrar) => setLoader((prev) => ({ ...prev, mostrar }));

  const cargarListado = () => {
    mostrarLoader(true);
    request
      .get(`Documentos/Listado?jdf=${encodeURIComponent(Number(financiera))}`)
      .then((result) => setListado(Array.isArray(result) ? result : []))
      .catch((error) => {
        setListado([]);
        mostrarError(error);
      })
      .finally(() => mostrarLoader(false));
  };

  useEffect(() => {
    setPagina(1);
    cargarListado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financiera]);

  useEffect(() => setPagina(1), [busqueda, filtros, porPagina]);

  // Pide confirmación y, si el usuario acepta, deshabilita el documento.
  // El endpoint regresa el listado actualizado, así que se usa su respuesta directamente.
  const onDeshabilitar = async (documento) => {
    const confirmacion = await Swal.fire({
      title: TITULO_SWAL,
      html: `¿Deshabilitar el documento <b>${escaparHtml(documento.documento)}</b>?<br>Dejará de solicitarse en las nuevas solicitudes.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, deshabilitar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#e24b4a",
      cancelButtonColor: "#8d9688",
      reverseButtons: true,
    });
    if (!confirmacion.isConfirmed) return;

    mostrarLoader(true);
    request
      .get(
        `${ENDPOINT_DESHABILITAR}?iddocumento=${encodeURIComponent(Number(documento.iddocumento))}` +
          `&jdf=${encodeURIComponent(Number(financiera))}`
      )
      .then((result) => {
        setListado(Array.isArray(result) ? result : []);
        Swal.fire(TITULO_SWAL, "Documento deshabilitado con éxito.", "success");
      })
      .catch(mostrarError)
      .finally(() => mostrarLoader(false));
  };

  const abrirModal = (iddocumento = SIN_EDICION) => setModal({ open: true, iddocumento });
  const cerrarModal = () => setModal({ open: false, iddocumento: SIN_EDICION });

  const onLimpiarFiltros = () => {
    setBusqueda("");
    setFiltros(FILTROS_INICIALES);
  };

  const listadoFiltrado = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return listado.filter(
      (row) =>
        (!texto || String(row.documento ?? "").toLowerCase().includes(texto)) &&
        (!filtros.tipopersona || row.tipopersona === filtros.tipopersona) &&
        (!filtros.linea_credito || row.linea_credito === filtros.linea_credito) &&
        coincideBandera(filtros.opcional, row.opcional) &&
        coincideBandera(filtros.fase2, row.fase2) &&
        coincideBandera(filtros.documento_cobranza, row.documento_cobranza)
    );
  }, [listado, busqueda, filtros]);

  const renglonesPagina = useMemo(() => {
    const inicio = (pagina - 1) * porPagina;
    return listadoFiltrado.slice(inicio, inicio + porPagina);
  }, [listadoFiltrado, pagina, porPagina]);

  const resumen = useMemo(
    () => ({
      total: listadoFiltrado.length,
      obligatorios: listadoFiltrado.filter((r) => !r.opcional).length,
      opcionales: listadoFiltrado.filter((r) => Boolean(r.opcional)).length,
      fase2: listadoFiltrado.filter((r) => Boolean(r.fase2)).length,
      cobranza: listadoFiltrado.filter((r) => Boolean(r.documento_cobranza)).length,
    }),
    [listadoFiltrado]
  );

  const kpis = [
    { clave: "total", etiqueta: "Total documentos", valor: resumen.total, estilo: "neutral" },
    { clave: "obligatorios", etiqueta: "Obligatorios", valor: resumen.obligatorios, estilo: "ok" },
    { clave: "opcionales", etiqueta: "Opcionales", valor: resumen.opcionales, estilo: "aviso" },
    { clave: "fase2", etiqueta: "Fase 2", valor: resumen.fase2, estilo: "info" },
    { clave: "cobranza", etiqueta: "Requeridos por cobranza", valor: resumen.cobranza, estilo: "cobranza" },
  ];

  const filtrosActivos = contarFiltrosActivos(filtros);
  const totalColumnas = 5 + COLUMNAS_BANDERA.length;

  return (
    <PlantillaScreenRemastered
      modulo="Crédito"
      titulo="Listado de documentos"
      className="screen-documentos-credito"
      onRegresar={() => history.push(RUTA_REGRESO)}
      filtro={({ cerrar }) => (
        <FiltroPanel
          tituloAlerta={TITULO_SWAL}
          campos={CAMPOS_FILTRO}
          valores={filtros}
          textoAplicar="Aplicar"
          onAplicar={(valores) => {
            setFiltros({ ...FILTROS_INICIALES, ...valores });
            cerrar();
          }}
          onCerrar={cerrar}
        />
      )}
      filtrosActivos={filtrosActivos}
      onAgregar={() => abrirModal()}
      agregarDeshabilitado={loader.mostrar}
    >
      <LoaderScreen loader={loader} />

      <ModalDocumentos
        open={modal.open}
        iddocumento={modal.iddocumento}
        financiera={financiera}
        onClose={cerrarModal}
        onGuardado={cargarListado}
      />

      <div className="sdoc-contenido">
        <div className="sdoc-kpis">
          {kpis.map((kpi) => (
            <div key={kpi.clave} className={`sdoc-kpi sdoc-kpi--${kpi.estilo}`}>
              <span className="sdoc-kpi-valor">{kpi.valor.toLocaleString("es-MX")}</span>
              <span className="sdoc-kpi-etiqueta">{kpi.etiqueta}</span>
            </div>
          ))}
        </div>

        <div className="sdoc-barra">
          <div className="sdoc-financieras" role="tablist" aria-label="Financiera">
            {FINANCIERAS_DOCUMENTOS.map((op) => (
              <button
                key={op.value}
                type="button"
                role="tab"
                aria-selected={financiera === op.value}
                className={`sdoc-financiera ${financiera === op.value ? "activa" : ""}`}
                onClick={() => setFinanciera(op.value)}
                disabled={loader.mostrar}
              >
                {op.label}
              </button>
            ))}
          </div>

          <div className="sdoc-buscador">
            <SearchIcon />
            <input
              type="text"
              placeholder="Buscar documento"
              value={busqueda}
              maxLength={100}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            {busqueda && (
              <button type="button" aria-label="Limpiar búsqueda" onClick={() => setBusqueda("")}>
                <ClearIcon />
              </button>
            )}
          </div>

          {(filtrosActivos > 0 || busqueda) && (
            <button type="button" className="sdoc-quitar-filtros" onClick={onLimpiarFiltros}>
              Quitar filtros
            </button>
          )}
        </div>

        <div className="sdoc-tabla scroll">
          <table cellPadding={0} cellSpacing={0}>
            <thead>
              <tr>
                <th>Documento</th>
                <th>Tipo de persona</th>
                <th>Línea de crédito</th>
                {COLUMNAS_BANDERA.map((col) => (
                  <th key={col.campo} className="sdoc-centro">{col.etiqueta}</th>
                ))}
                <th className="sdoc-num">Vigencia (días)</th>
                <th className="sdoc-centro">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {renglonesPagina.length === 0 ? (
                <tr>
                  <td className="sdoc-vacio" colSpan={totalColumnas}>
                    {loader.mostrar ? "Cargando información..." : "No se encontraron documentos con los filtros seleccionados."}
                  </td>
                </tr>
              ) : (
                renglonesPagina.map((row) => (
                  <tr key={row.iddocumento}>
                    <td className="sdoc-documento" title={row.documento}>{row.documento}</td>
                    <td>
                      <span className="sdoc-chip">{etiquetaCatalogo(TIPOS_PERSONA_DOCUMENTO, row.tipopersona) || "—"}</span>
                    </td>
                    <td>
                      <span className="sdoc-chip sdoc-chip--linea">
                        {etiquetaCatalogo(LINEAS_CREDITO_DOCUMENTO, row.linea_credito) || "—"}
                      </span>
                    </td>
                    {COLUMNAS_BANDERA.map((col) => (
                      <td key={col.campo} className="sdoc-centro">
                        <Bandera activo={Boolean(row[col.campo])} />
                      </td>
                    ))}
                    <td className="sdoc-num">{row.dias_vigencia ?? "—"}</td>
                    <td className="sdoc-centro">
                      <div className="sdoc-acciones">
                        <button
                          type="button"
                          className="sdoc-btn-accion"
                          onClick={() => abrirModal(row.iddocumento)}
                          aria-label={`Editar ${row.documento}`}
                        >
                          <EditOutlinedIcon /> Editar
                        </button>
                        <button
                          type="button"
                          className="sdoc-btn-accion sdoc-btn-accion--baja"
                          onClick={() => onDeshabilitar(row)}
                          disabled={loader.mostrar}
                          aria-label={`Deshabilitar ${row.documento}`}
                        >
                          <BlockIcon /> Deshabilitar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginadorListado
          pagina={pagina}
          totalRegistros={listadoFiltrado.length}
          porPagina={porPagina}
          onCambiarPagina={setPagina}
          onCambiarPorPagina={setPorPagina}
        />
      </div>
    </PlantillaScreenRemastered>
  );
};
